import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import Popper from 'popper.js';
import TooltipContext from './TooltipContext';

const TooltipWrapper = ({ children, preventOverflow }) => {
  const popperRef = useRef();
  const tooltipRef = useRef();
  const triggerRef = useRef();
  const arrowRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [currentPlacement, setCurrentPlacement] = useState('top');
  const [popperStyle, setPopperStyle] = useState({});
  const [arrowStyles, setArrowStyles] = useState({});

  const show = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    setIsVisible(false);
  }, []);

  const destroyPopper = () => {
    if (popperRef && popperRef.current) {
      popperRef.current.destroy();
      popperRef.current = null;
    }
  };

  useEffect(() => {
    if (popperRef.current) destroyPopper();
    if (isVisible) {
      popperRef.current = new Popper(triggerRef.current, tooltipRef.current, {
        positionFixed: true,
        placement: 'top',
        modifiers: {
          applyStyle: { enabled: false },
          offset: { enabled: false },
          arrow: { element: arrowRef.current },
          updateStateMod: {
            enabled: true,
            order: 900,
            fn(data) {
              setPopperStyle({ ...data.styles });
              setArrowStyles(data.arrowStyles);
              setCurrentPlacement(data.placement);
              return data;
            },
          },
          preventOverflow: {
            enabled: !preventOverflow,
          },
          hide: {
            enabled: false,
          },
        },
      });
      popperRef.current.scheduleUpdate();
    }
    return () => {
      if (popperRef.current) destroyPopper();
    };
  }, [triggerRef, isVisible, preventOverflow]);

  const tooltipContextValues = useMemo(
    () => ({
      isVisible,
      show,
      hide,
      tooltipRef,
      popperStyle,
      placement: currentPlacement,
      triggerRef,
      arrow: {
        style: arrowStyles,
        ref: arrowRef,
      },
    }),
    [
      isVisible,
      show,
      hide,
      tooltipRef,
      popperStyle,
      currentPlacement,
      triggerRef,
      arrowStyles,
      arrowRef,
    ],
  );

  return <TooltipContext.Provider value={tooltipContextValues}>{children}</TooltipContext.Provider>;
};

TooltipWrapper.defaultProps = {
  preventOverflow: false,
};

TooltipWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  preventOverflow: PropTypes.bool,
};

export default TooltipWrapper;
